import DOMPurify from 'dompurify';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import { ConfirmProvider, ToastProvider } from '@payforge/ui';

// Register a Trusted Types default policy backed by DOMPurify. This satisfies
// `require-trusted-types-for 'script'` in the CSP: any plain-string HTML sink
// (e.g. React's dangerouslySetInnerHTML, third-party libs) is sanitized via
// DOMPurify before the browser accepts it. Browsers without TT support skip the
// block entirely.
// TRUSTED_TYPES_POLICY: null disables DOMPurify's internal TT policy creation for
// this call — necessary here to avoid creating a second 'dompurify' policy while
// already inside a policy callback (re-entrancy guard). The browser converts the
// returned sanitized string to TrustedHTML automatically.
if (window.trustedTypes && window.trustedTypes.createPolicy) {
  window.trustedTypes.createPolicy('default', {
    createHTML: (input: string) =>
      DOMPurify.sanitize(input, { TRUSTED_TYPES_POLICY: null }),
    createScript: () => {
      throw new Error('Trusted Types: scripts are not allowed');
    },
    createScriptURL: () => {
      throw new Error('Trusted Types: script URLs are not allowed');
    },
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('#root element not found in index.html');
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      // Global default: queries are fresh for 60 s; don't repeat this per-site.
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <ConfirmProvider>
            <App />
          </ConfirmProvider>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
